import { Tab } from "@chakra-ui/tabs";
import { keepPreviousData } from "@tanstack/react-query";
import {
  useDistributionBreakdownTableDataRetrieve,
  useDistributionUnfundedBreakdownTableDataRetrieve,
} from "gen/endpoints/distributions/distributions";
import { noop } from "lodash-es";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LoadingState } from "shared/components/empty/LoadingState";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

export const FundFamilyDistributionBreakdownTable: React.FC<{
  fundFamilyId: string;
  fundFamilyEventId: string;
}> = ({ fundFamilyId, fundFamilyEventId }) => {
  const { t } = useTranslation(["common", "distribution"]);

  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const {
    data: distributionBreakdownTableData,
    isFetching: isBreakdownLoading,
  } = useDistributionBreakdownTableDataRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      is_equalized_view: isEqualizedView || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const {
    data: distributionUnfundedBreakdownTableData,
    isFetching: isUnfundedBreakdownLoading,
  } = useDistributionUnfundedBreakdownTableDataRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      is_equalized_view: isEqualizedView || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const selectedTabId: "distribution" | "unfunded" =
    selectedTab === 0 ? "distribution" : "unfunded";

  const tableData =
    selectedTabId === "distribution"
      ? distributionBreakdownTableData
      : distributionUnfundedBreakdownTableData;

  return (
    <TableBox isFullWidth={true} key={selectedTabId}>
      <TableBoxHeader>
        <TableBoxTabs
          index={selectedTab}
          onChange={setSelectedTab}
          colorScheme="gray"
        >
          <Tab>
            {t("distribution:distributionBreakdown.distributionSummary")}
          </Tab>
          <Tab>{t("distribution:distributionBreakdown.unfunded")}</Tab>
        </TableBoxTabs>
        <DataTableBoxActions>
          <EqualizedViewToggleButton
            isEqualizedView={isEqualizedView}
            setIsEqualizedView={setIsEqualizedView}
          />
        </DataTableBoxActions>
      </TableBoxHeader>
      <TableBoxBody>
        {tableData ? (
          <DataTable
            data={tableData}
            isLoading={
              selectedTabId === "distribution"
                ? isBreakdownLoading
                : isUnfundedBreakdownLoading
            }
          />
        ) : (
          <LoadingState />
        )}
      </TableBoxBody>
    </TableBox>
  );
};

const FundFamilyDistributionBreakdownForm: React.FC<{
  fundFamilyId: string;
  fundFamilyEventId: string;
}> = ({ fundFamilyId, fundFamilyEventId }) => {
  const { navigateNext } = usePageWizard();

  const form = useForm();
  return (
    <Form form={form} onSubmit={noop} onSuccess={navigateNext}>
      <FundFamilyDistributionBreakdownTable
        fundFamilyId={fundFamilyId}
        fundFamilyEventId={fundFamilyEventId}
      />
      <PageWizardFormFooter />
    </Form>
  );
};

export const FundFamilyDistributionBreakdownPage: React.FC = () => {
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
  return (
    <PageBody>
      <FundFamilyDistributionBreakdownForm
        fundFamilyId={fundFamilyId}
        fundFamilyEventId={fundFamilyEventId}
      />
    </PageBody>
  );
};
